import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';

import {
  CenteredLoader,
  InformationBox,
  Toggle,
  useToast,
  Button,
  useBoolModalState,
} from 'components';
import {
  Criterion,
  IaPromptName,
  IaTargetResult,
  OfferType,
  ParticipantFieldCategory,
  Recruitment,
  Session,
  useOffers,
  useParticipantFieldsByCategory,
  useParticipantFieldsByName,
  usePlan,
  usePlanCapabilities,
} from '@app/api';

import { SessionFormSection } from './components/SessionFormSection';
import { TargetingCriteriaSelectionSection } from './components/Criteria';
import {
  useMarkStudyStepAsValidated,
  useRecruitment,
  useTargetingPlanOffer,
  useUpdateCriteria,
  useUpdateRecruitment,
} from 'queries';
import { EditSessionLayout } from './components/EditSessionLayout';
import { SessionSubmitLine } from './components/SessionSubmitLine';
import { useRedirectIfDraft } from './hooks/useRedirectIfDraft';
import { TargetingSection } from './components/TargetingSection';
import { UpsellTargetingModal } from 'components/Features/UpsellTargetingModal';
import { filterOffers } from 'pages/StudySetup/GeneralSetup/filterOffers';
import { areQuotasValid } from '/lib/criterionQuotaUtils';
import { useIaSuggestion } from './hooks/useIaSuggestion';
import { IaSuggestionButton } from './components/IaSuggestionButton';
import { TargetIaSuggestion } from './components/TargetIaSuggestion';
import { applyIaTargetCriteria, IaTargetCriterion } from 'lib/iaTargetUtils';

const useStyles = createUseStyles((theme: Theme) => ({
  page: {
    '&:last-child': {
      paddingBottom: theme.marginBase * 30,
    },
  },
  infoBox: {
    marginTop: theme.marginBase * 3,
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.marginBase * 2,
  },
}));

interface Props {
  session: Session;
}

const categories: ParticipantFieldCategory[] = [
  ParticipantFieldCategory.DEMOGRAPHIC,
  ParticipantFieldCategory.GEOLOCATION,
  ParticipantFieldCategory.DIGITAL_HABITS,
  ParticipantFieldCategory.PROFESSIONAL,
];

export const EditSessionTarget = ({ session }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const toast = useToast();
  const { t } = useTranslation();

  const [upsellModalOpened, openUpsellModal, closeUpsellModal] =
    useBoolModalState();
  const { data: capabilities } = usePlanCapabilities(session.planId);
  const { data: plan } = usePlan(session.planId);
  const offer = useTargetingPlanOffer(session.planId);

  const { data: allOffers } = useOffers();
  const showUpsell = useMemo(() => {
    if (!session || !allOffers || !offer) return false;
    return (
      filterOffers(
        { ...session, priceThierId: plan?.priceThierId },
        allOffers,
      ).filter(
        (o) =>
          o.type === OfferType.TARGETING &&
          o.products.find((p) => !p.forRecruitmentByClient) &&
          o.prices?.perParticipant > offer.prices?.perParticipant,
      ).length > 0
    );
  }, [allOffers, offer, plan]);

  const { data: recruitment, isLoading: recruitmentLoading } = useRecruitment(
    session.recruitmentId,
  );
  const ppFieldsByCategory = useParticipantFieldsByCategory();
  const ppFieldsByName = useParticipantFieldsByName();

  const [criteria, _setCriteria] = useState(recruitment?.criteria || []);

  const setCriteria = useCallback(
    (criteria: Criterion[]) => {
      _setCriteria(criteria);
    },
    [_setCriteria],
  );

  const {
    available: iaSuggestionAvailable,
    attempts: iaSuggestionAttempts,
    ready: iaSuggestionReady,
    request: requestIaSuggestion,
    requestFirst: requestFirstIaSuggestion,
    generating: iaSuggestionGenerating,
  } = useIaSuggestion<IaTargetResult>(session, IaPromptName.TARGET);
  const hasIaCriteria = iaSuggestionAttempts.length > 0;
  const [iaSuggestionOpen, setIaSuggestionOpen] = useState(false);
  const hasCriteria = useMemo(
    () =>
      criteria.some((criterion) => {
        const field = ppFieldsByName[criterion.fieldRef];
        return !!field && !field.criterion?.locked;
      }),
    [criteria, ppFieldsByName],
  );
  const showIaSuggestion = iaSuggestionAvailable && !hasCriteria;

  const [allowAlreadyParticipated, setAllowAlreadyParticipated] = useState<
    boolean | undefined
  >(undefined);
  const [excludeTechParticipants, setExcludeTechParticipants] = useState(false);
  const { mutateAsync: updateRecruitment, isLoading: isRecruitmentSaving } =
    useUpdateRecruitment(session.recruitmentId);

  const { mutateAsync: updateCriteria, isLoading: isSaving } =
    useUpdateCriteria(session.recruitmentId);
  const redirectIfDraft = useRedirectIfDraft(session);
  const { mutateAsync: markStepAsValidated } = useMarkStudyStepAsValidated(
    session.id,
  );

  const updated = useRef(false);
  useEffect(() => {
    if (recruitment && !updated.current) {
      setCriteria(recruitment.criteria);
      updated.current = false;
    }
  }, [recruitment, setCriteria]);
  useEffect(() => {
    if (recruitment) {
      setExcludeTechParticipants(!recruitment.allowTechParticipants);
    }
  }, [recruitment]);

  const acceptIaSuggestion = useCallback(
    (items: IaTargetCriterion[]) => {
      updated.current = true;
      setCriteria(applyIaTargetCriteria(criteria, items));
      setIaSuggestionOpen(false);
      toast.success(t('editSession.targeting.iaSuggestion.added'));
    },
    [criteria, setCriteria, toast, t],
  );

  const onSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      if (!areQuotasValid(criteria)) {
        toast.error(t('editSession.targeting.quotas.rangeNotCovered'));
        return;
      }

      try {
        await updateCriteria(criteria);
        let recruitmentValues: Partial<Recruitment> = {
          allowTechParticipants: !excludeTechParticipants,
        };
        if (allowAlreadyParticipated !== undefined) {
          recruitmentValues = {
            ...recruitmentValues,
            allowAlreadyParticipated,
          };
        }
        await updateRecruitment(recruitmentValues);
        await markStepAsValidated({ step: 'targeting' });
        toast.saved();
        redirectIfDraft();
      } catch (e) {
        toast.exception(e);
      }
    },
    [
      criteria,
      toast,
      t,
      updateCriteria,
      excludeTechParticipants,
      allowAlreadyParticipated,
      updateRecruitment,
      markStepAsValidated,
      redirectIfDraft,
    ],
  );

  const isLoading = recruitmentLoading || !capabilities;

  return (
    <form onSubmit={onSubmit}>
      <EditSessionLayout
        session={session}
        footer={
          <SessionSubmitLine
            session={session}
            saving={isSaving || isRecruitmentSaving}
          />
        }
      >
        <SessionFormSection
          title={t('editSession.targeting.title')}
          className={classes.page}
        >
          {showUpsell && (
            <InformationBox variant="info" className={classes.infoBox}>
              {t('editSession.targeting.upsell.text', {
                offer: offer?.name,
              })}
              <div className={classes.buttons}>
                <Button
                  variant="muted"
                  color="blue"
                  onClick={openUpsellModal}
                  small
                >
                  {t('editSession.targeting.upsell.change')}
                </Button>
              </div>
            </InformationBox>
          )}
          {!isLoading && showIaSuggestion && (
            <IaSuggestionButton
              text={t('editSession.targeting.iaSuggestion.generate')}
              isLoading={
                !iaSuggestionReady || (iaSuggestionGenerating && !hasIaCriteria)
              }
              onClick={() => {
                requestFirstIaSuggestion();
                setIaSuggestionOpen(true);
              }}
            />
          )}
          {isLoading ? (
            <CenteredLoader />
          ) : (
            categories.map((category) => (
              <TargetingCriteriaSelectionSection
                key={category}
                category={category}
                fields={ppFieldsByCategory[category]}
                allowedFields={offer?.criteria}
                criteria={criteria}
                onCriteriaChange={setCriteria}
                onRequestUpsellModalOpen={openUpsellModal}
                numberOfParticipantRequired={
                  session.numberOfParticipantRequired
                }
              />
            ))
          )}
          <TargetingSection title={t('editSession.targeting.preferences')}>
            <Toggle
              label={t('editSession.targeting.allowAlreadyParticipated')}
              checked={
                allowAlreadyParticipated === undefined && recruitment
                  ? recruitment.allowAlreadyParticipated
                  : allowAlreadyParticipated || false
              }
              onChange={setAllowAlreadyParticipated}
            />
            <Toggle
              label={t('editSession.targeting.excludeTechParticipants')}
              checked={excludeTechParticipants}
              onChange={setExcludeTechParticipants}
            />
          </TargetingSection>
        </SessionFormSection>
      </EditSessionLayout>
      <TargetIaSuggestion
        attempts={iaSuggestionAttempts}
        criteria={criteria}
        allowedFields={offer?.criteria}
        generating={iaSuggestionGenerating}
        isOpen={iaSuggestionOpen}
        onClose={() => setIaSuggestionOpen(false)}
        onRetry={requestIaSuggestion}
        onAccept={acceptIaSuggestion}
      />
      <UpsellTargetingModal
        isOpen={upsellModalOpened}
        onRequestClose={closeUpsellModal}
        sessionId={session.id}
      />
    </form>
  );
};
